import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';
import { formatEmployeeName } from '../utils';
import { NameDisplayFormat } from '../../shared/types';

interface UnassignedEmployee {
    emp_number: number;
    emp_firstname: string;
    emp_lastname: string;
}

interface AddNewEmployeeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { emp_number: number, pin: string }) => void;
}

const AddNewEmployeeModal: React.FC<AddNewEmployeeModalProps> = ({ isOpen, onClose, onSave }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [selectedEmpNumber, setSelectedEmpNumber] = useState<number | ''>('');
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [unassignedEmployees, setUnassignedEmployees] = useState<UnassignedEmployee[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const fetchUnassigned = async () => {
                setIsLoading(true);
                try {
                    const response = await fetch('/api/orangehrm/employees/unassigned');
                    if (!response.ok) throw new Error('Failed to fetch employees');
                    const data = await response.json();
                    setUnassignedEmployees(data);
                    if (data.length > 0) {
                        setSelectedEmpNumber(data[0].emp_number);
                    } else {
                        setSelectedEmpNumber('');
                    }
                } catch (e: any) {
                    setError(e.message);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchUnassigned();
            setPin('');
            setError('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                formRef.current?.requestSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onSave]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!selectedEmpNumber) {
            setError('Please select an employee.');
            return;
        }
        if (pin.length < 4) {
            setError('PIN must be at least 4 digits.');
            return;
        }
        const selectedEmployee = unassignedEmployees.find(e => e.emp_number === selectedEmpNumber);
        if (selectedEmployee) {
            onSave({ 
                emp_number: selectedEmpNumber, 
                pin,
            });
        }
    };

    const inputClass = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    const labelClass = "block text-sm font-medium text-gray-600 dark:text-gray-300";

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Enable Kiosk Access for Employee">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                {error && <p className="text-red-600 dark:text-red-500 bg-red-100 dark:bg-red-900/50 p-3 rounded-md text-center font-semibold">{error}</p>}
                <div>
                    <label htmlFor="add-employee-select" className={labelClass}>Employee</label>
                    {isLoading ? <p>Loading employees...</p> : (
                         <select 
                            id="add-employee-select" 
                            value={selectedEmpNumber} 
                            onChange={e => setSelectedEmpNumber(Number(e.target.value))} 
                            required 
                            className={inputClass}
                         >
                            {unassignedEmployees.length === 0 && <option value="">No unassigned employees found</option>}
                            {unassignedEmployees.map(emp => (
                                <option key={emp.emp_number} value={emp.emp_number}>
                                    {formatEmployeeName({ firstName: emp.emp_firstname, lastName: emp.emp_lastname }, NameDisplayFormat.LAST_FIRST)}
                                </option>
                            ))}
                         </select>
                    )}
                </div>
                
                <div>
                    <label htmlFor="add-pin" className={labelClass}>Assign PIN (4+ digits)</label>
                    <input type="password" id="add-pin" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} required minLength={4} className={inputClass} />
                </div>
                <div className="flex justify-end gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit" disabled={isLoading || unassignedEmployees.length === 0}>Enable Employee</Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddNewEmployeeModal;
